// web/src/components/ShareModal.tsx
import React, { useState, useEffect } from 'react';
import { Copy, Check, X, Link, Trash2 } from 'react-feather';

interface ShareModalProps {
  conversationId: string;
  onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ conversationId, onClose }) => {
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // Start with loading to check for existing
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for existing share link on mount
  useEffect(() => {
    const checkExistingShare = async () => {
      try {
        const response = await fetch(`/api/conversations/${conversationId}/share`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.shareUrl) {
            const fullUrl = `${window.location.origin}${data.data.shareUrl}`;
            setShareUrl(fullUrl);
          }
        }
      } catch (err) {
        // Ignore errors - just means no existing share link
      } finally {
        setLoading(false);
      }
    };

    checkExistingShare();
  }, [conversationId]);

  const createShareLink = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/conversations/${conversationId}/share`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to create share link');
      }

      const data = await response.json();
      const fullUrl = `${window.location.origin}${data.data.shareUrl}`;
      setShareUrl(fullUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const revokeShareLink = async () => {
    try {
      await fetch(`/api/conversations/${conversationId}/share`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setShareUrl(null);
    } catch (err) {
      setError('Failed to revoke share link');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-gray-900 font-semibold">
            <Link size={18} className="text-eddy-blue" />
            Share Conversation
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          {loading && !shareUrl ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-2 border-eddy-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !shareUrl ? (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Anyone with the link can view this conversation.
              </p>
              <p className="text-gray-500 text-xs">
                If they're signed in and reply, it will create their own copy.
              </p>
              <button
                onClick={createShareLink}
                disabled={loading}
                className="w-full py-2.5 px-4 bg-eddy-blue text-white rounded-lg font-medium
                           hover:bg-eddy-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Share Link'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                This conversation has a share link. Anyone with the link can view it.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg
                             text-gray-700 font-mono truncate"
                />
                <button
                  onClick={copyToClipboard}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm
                              transition-colors ${
                                copied
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              <button
                onClick={revokeShareLink}
                className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
              >
                <Trash2 size={14} />
                Revoke share link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
